import { useCallback, useEffect, useMemo, useState } from "react"
import { GetClaimSignatureResponse } from "@mezo/api"
import { CHAIN_ID, TAHO_NFT_CONTRACT } from "@mezo/sdk"
import { PayloadAction } from "@reduxjs/toolkit"
import { Eip1193Provider } from "ethers"
import { useAccount, useReadContract } from "wagmi"

import {
  fetchMezoBalances,
  getCollectedProducts,
  getTahoClaimSignature,
  orderProduct,
  selectCollectedProducts,
  selectCurrentProduct,
  selectOpenModal,
  selectUsername,
  selectWalletBalances,
  setOpenModal,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"

import { MEZO_NETWORK } from "../constants"
import { floatToBigInt } from "../utils"

import { useIsTahoWallet } from "./custom"

export const useOrderProduct = () => {
  const currentProduct = useDappSelector(selectCurrentProduct)

  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useDappDispatch()

  const orderItemHandler = useCallback(async () => {
    if (!currentProduct) return

    setIsLoading(true)

    const { payload: success } = (await dispatch(
      orderProduct({
        productId: currentProduct.id,
        price: floatToBigInt(currentProduct.price),
      }),
    )) as PayloadAction<boolean>

    setIsLoading(false)

    if (success) {
      await dispatch(getCollectedProducts())
      await dispatch(fetchMezoBalances())
      dispatch(setOpenModal("store-item-success"))
    }
  }, [currentProduct, dispatch])

  return { orderItem: orderItemHandler, isLoading }
}

export const useTahoNFTClaim = () => {
  const { connector, address, status } = useAccount()
  const dispatch = useDappDispatch()
  const { MUSD, mBTC } = useDappSelector(selectWalletBalances)
  const userName = useDappSelector(selectUsername)
  const openModal = useDappSelector(selectOpenModal)

  const isTaho = useIsTahoWallet()

  const [claimerId, setClaimerId] = useState<string | null>(null)

  useEffect(() => {
    if (isTaho && status === "connected") {
      const getTahoClaimId = async () => {
        const provider = (await connector.getProvider()) as Eip1193Provider

        /**
         * Non-standard rpc method meant to be used only with Taho
         */
        const result = await provider.request({
          method: "tally_getMezoClaimData",
          params: [],
        })

        const [installId] = result

        setClaimerId(installId)
      }

      getTahoClaimId()
    } else {
      setClaimerId(null)
    }
  }, [isTaho, dispatch, connector, status, MUSD.balance])

  const [claimSignature, setClaimSignature] =
    useState<GetClaimSignatureResponse | null>(null)

  useEffect(() => {
    if (
      claimerId &&
      address &&
      userName !== "" &&
      openModal === "taho-nft-store-item" &&
      mBTC.balance >= 0n
    ) {
      const getSignature = async () => {
        const { payload: signature } = (await dispatch(
          getTahoClaimSignature({ claimerId, address }),
        )) as PayloadAction<GetClaimSignatureResponse>

        setClaimSignature(signature)
      }

      getSignature()
    }
  }, [dispatch, claimerId, address, userName, openModal, mBTC.balance])

  const hasEnoughFunds = useMemo(() => {
    const NFT_PRICE = floatToBigInt(100, Number(MUSD.decimals))

    return MUSD.balance >= NFT_PRICE
  }, [MUSD])

  const { data: isClaimed, refetch: checkIsClaimed } = useReadContract({
    chainId: CHAIN_ID[MEZO_NETWORK],
    address: TAHO_NFT_CONTRACT.address[MEZO_NETWORK] as `0x${string}`,
    abi: [
      {
        type: "function",
        name: "claimedIds",
        stateMutability: "view",
        inputs: [{ name: "id", type: "string" }],
        outputs: [{ type: "bool" }],
      },
    ],
    functionName: "claimedIds",
    args: [claimerId!],
    query: { enabled: claimerId !== null },
  })

  const hasValidClaimSignature =
    !!claimSignature &&
    claimSignature !== "not-eligible" &&
    claimSignature !== "invalid-claim-address"

  return {
    claimerId,
    claimSignature,
    /**
     * User eligible and on valid address
     */
    hasValidClaimSignature,
    hasEnoughFunds,
    isTaho,
    // if we have a signature then user is eligible
    isClaimed: hasValidClaimSignature && !!isClaimed,
    checkIsClaimed,
  }
}

export const useMarketItemModalButton = () => {
  const currentProduct = useDappSelector(selectCurrentProduct)
  const collectedProducts = useDappSelector(selectCollectedProducts)
  const { MUSD } = useDappSelector(selectWalletBalances)

  const isCollected = useMemo(
    () => currentProduct && collectedProducts.includes(currentProduct.id),
    [currentProduct, collectedProducts],
  )

  const hasEnoughFunds = useMemo(() => {
    if (!currentProduct) return false

    return MUSD.balance >= floatToBigInt(currentProduct.price)
  }, [MUSD, currentProduct])

  const buttonLabel = useMemo(() => {
    if (!hasEnoughFunds) {
      return "Not enough funds"
    }

    return isCollected ? "Collected" : "Collect"
  }, [hasEnoughFunds, isCollected])

  return {
    marketItemModalButtonLabel: buttonLabel,
    isButtonDisabled: isCollected || !hasEnoughFunds,
  }
}
